import { useEffect, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import type { ApiClient, CompletionPlanItem } from '../api';
import { Reiter } from './koordination/Reiter';
import { PageHeader } from '../rahmen/PageHeader';
import {
  DataTable, EmptyState, ErrorState, LoadingState,
} from '../muster';
import { Baum } from './koordination/Baum';
import { Filterleiste, type Filter } from './koordination/Filterleiste';
import { Kollisionsliste } from './koordination/Kollisionsliste';
import { Grundlagen } from './koordination/Grundlagen';
import { Auswertung } from './koordination/Auswertung';
import { KollisionenUndEinstufung } from './koordination/KollisionenUndEinstufung';
import { Planeraufgaben } from './koordination/Planeraufgaben';
import { Arbeitsvorrat } from './koordination/Arbeitsvorrat';

/**
 * Kollision und Koordination — Stufe 1.
 *
 * --- Was dieses Modul von einer Kollisionsprüfung unterscheidet -------------
 *
 * Nicht das Finden ist schwer, sondern das Aussortieren. Ein üblicher
 * Kollisionsbericht hat vierzigtausend Treffer, davon achtunddreißigtausend
 * irrelevant.
 *
 * Navisworks weiß nicht, dass an dieser Stelle eine Aussparung geplant ist.
 * Diese Plattform weiß es — 5.665 conVoid-Aussparungen sind erfasst — und
 * stuft die Durchdringung als vorgesehen ein statt als Fehler.
 *
 * --- Warum die Grundlagen VOR dem ersten Lauf stehen ------------------------
 *
 * Die Einstufung entscheidet über alles. Wird „echt" die größte Gruppe, sind
 * nicht zwanzigtausend Kollisionen entstanden — dann greifen die Regeln
 * nicht.
 *
 * Deshalb stehen Regeln, Toleranzen und der Montagezuschlag im Reiter
 * „Grundlagen", mit Herkunft und Begründung. Wer die Zahlen später bewerten
 * soll, muss vorher wissen, wogegen gerechnet wurde.
 *
 * --- Warum die Seite in eigene Komponenten aufgeteilt ist -------------------
 *
 * Eine 2.300-Zeilen-Datei liest niemand mehr im Ganzen. Jede Karte lebt jetzt
 * unter seiten/koordination/ als eigene Komponente mit expliziten Props
 * (Daten + Rückrufe) — kein Zustand wandert über die Modulgrenze. Zustand und
 * Ladeeffekte bleiben HIER, in der einen Stelle, die weiß, was wann geholt
 * wird.
 *
 * --- Batch 7 — vier Reiter statt sieben Abschnitte --------------------------
 *
 * Der Fachplaner wollte einen Planerarbeitsplatz mit Baum, Filtern,
 * Kategorien und Reitern statt einer langen Seite mit Sprungleiste. Die
 * bestehenden sieben Abschnitte sind NICHT verschwunden — sie stehen jetzt
 * unter vier Reitern (Arbeiten/Planeraufgaben/Auswertung/Grundlagen), der
 * aktive Reiter steht in der URL (`?reiter=`). „Kollisionen und Einstufung"
 * (Bestand, Lücken) und „Abarbeitung" (Arbeitsvorrat samt Stufe-2-Rechnung)
 * bleiben unverändert im Reiter „Arbeiten“, unterhalb des NEUEN Baum- und
 * Listen-Arbeitsplatzes — beides bleibt erreichbar, nichts geht verloren.
 */

interface Modell {
  id: string;
  name: string;
  isActive?: boolean;
  revitVersion: number;
  rollen?: string[];
}

interface Rahmen {
  api: ApiClient;
  projekt?: { id: string; modelle: Modell[] };
}

export function Koordination() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [suchparams, setSuchparams] = useSearchParams();

  const [d, setD] = useState<any | null>(null);
  const [k, setK] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [suche, setSuche] = useState<any | null>(null);
  const [bericht, setBericht] = useState<any | null>(null);

  // Der Arbeitsvorrat: Zählwerte immer, Einzelfälle nur für den gewählten
  // Korb. 'korb' ist null, solange niemand einen aufgeklappt hat - dann
  // holt der Endpunkt auch keine Fälle.
  const [vorrat, setVorrat] = useState<any | null>(null);

  // ════════════════════════════════════════════════════════════════════
  // LADEFEHLER SICHTBAR MACHEN
  // ════════════════════════════════════════════════════════════════════
  //
  // Hier stand nichts, und das hat drei Tage gekostet. Die Endpunkte wurden
  // so geladen:
  //
  //     api.koordinationVorschlaege(...).catch(() => setVorschlaege(null))
  //
  // Ein stummes catch. Als /vorschlaege am 17.08. anfing, HTTP 500 zu
  // liefern — eine EF-Abfrage mit fünf Distinct() in einer Projektion, die
  // sich nicht übersetzen lässt —, sah das für den Anwender genauso aus wie
  // "keine Vorschläge vorhanden". Die Seite war leer und sagte nicht, warum.
  //
  // Ein Fehler, den niemand sieht, ist teurer als einer, der laut ist.
  const [ladefehler, setLadefehler] = useState<Record<string, string>>({});

  const merkeFehler = (was: string) => (e: unknown) => {
    setLadefehler((f) => ({
      ...f,
      [was]: e instanceof Error ? e.message : String(e),
    }));

    return null;
  };
  const [korb, setKorb] = useState<string | null>(null);
  const [korbseite, setKorbseite] = useState(0);

  // Das Detail EINER Kollision. null heisst "nichts aufgeklappt" - dann wird
  // auch nichts geholt.
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLaedt, setDetailLaedt] = useState<string | null>(null);
  const [sucht, setSucht] = useState(false);
  const [stufe2, setStufe2] = useState<any | null>(null);
  const [vorschlaege, setVorschlaege] = useState<any | null>(null);

  // --- Lösungen und Planeraufgaben (Loesungsableitung) ------------------
  const [loesungen, setLoesungen] = useState<any | null>(null);
  const [ableitetLaeuft, setAbleitetLaeuft] = useState(false);
  const [planeraufgaben, setPlaneraufgaben] = useState<any | null>(null);
  const [planeraufgabeStatus, setPlaneraufgabeStatus] = useState('');
  const [planeraufgabeOffen, setPlaneraufgabeOffen] = useState<string | null>(null);
  const [planeraufgabeDetail, setPlaneraufgabeDetail] = useState<any | null>(null);
  const [planeraufgabeEntscheidungstext, setPlaneraufgabeEntscheidungstext] =
    useState('');
  const [planeraufgabeErlaeuternLaeuft, setPlaneraufgabeErlaeuternLaeuft] =
    useState(false);

  // ════════════════════════════════════════════════════════════════════
  // DIE FILTER — WEIL EINE LISTE ÜBER DAS GANZE BAUWERK KEINE IST
  // ════════════════════════════════════════════════════════════════════
  //
  // Gemeldet am 18.08.2026: "die lösungen sollen gefiltert werden können
  // nach abschnitt, geschoss, gewerk, lösungsart".
  //
  // Der Planer arbeitet einen Abschnitt in einem Geschoss ab. Eine
  // ungefilterte Liste mit zehntausenden Zeilen beantwortet seine Frage
  // nicht — sie zeigt ihm die ersten zweihundert, und die liegen über das
  // ganze Bauwerk verstreut.
  //
  // Die Auswahlwerte kommen aus den DATEN und nicht aus einer Liste im
  // Code: Ein Geschoss, das es gibt und in der Auswahl fehlt, ist nicht
  // filterbar; eines in der Auswahl, das es nicht gibt, liefert eine leere
  // Liste und sieht aus wie ein Fehler.
  const [vf, setVf] = useState<{
    befund: string; gewerk: string; geschoss: string;
    bereich: string; richtung: string; seite: number;
  }>({ befund: '', gewerk: '', geschoss: '',
       bereich: '', richtung: '', seite: 1 });
  const [rechnet, setRechnet] = useState(false);
  const [abstaende, setAbstaende] = useState<any | null>(null);

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7 — DER REITER (URL-SYNCHRONISIERT)
  // ════════════════════════════════════════════════════════════════════
  const reiter = suchparams.get('reiter') || 'arbeiten';

  const reiterWaehlen = (r: string) => {
    const neu = new URLSearchParams(suchparams);
    neu.set('reiter', r);
    setSuchparams(neu, { replace: true });
  };

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7 — DER BAUM (Geschoss → Abschnitt → Quadrant → Gewerkpaar, oder
  // Geschoss → Abschnitt → Planeraufgabe)
  // ════════════════════════════════════════════════════════════════════
  const [baum, setBaum] = useState<any | null>(null);
  const [baumLaedt, setBaumLaedt] = useState(false);

  // ════════════════════════════════════════════════════════════════════
  // N6 — BAUMAUSWAHL IN DER URL, NICHT MEHR IN useState
  // ════════════════════════════════════════════════════════════════════
  //
  // Gemessen (N6): 'baumArt' und 'baumGewaehlt' sind der Zustand, den der
  // Baum tatsächlich mit der Liste teilt — der Baum SCHREIBT ihn, der
  // Ladeeffekt der Liste LIEST ihn (Abhängigkeit weiter unten). Filter und
  // Seite standen schon vorher in 'suchparams' (Batch 7); diese zwei fehlten
  // noch. Jetzt dieselbe Quelle: eine Baumauswahl ist damit verlinkbar und
  // überlebt ein Neuladen der Seite, genau wie Filter und Seite es schon
  // taten.
  //
  // 'detail' (die offene Kollision) bleibt bewusst NICHT in der URL - sie
  // ist zwischen Kollisionsliste UND Arbeitsvorrat dieselbe Variable
  // (Abarbeitung weiter unten teilt sie sich), und 'listeEntscheiden'
  // schliesst/öffnet sie zweimal hintereinander, um den neuen Status
  // anzuzeigen (siehe deren Kommentar). Das in einen URL-Parameter zu
  // heben, hiesse diesen Umschalt-Trick durch einen Versionszähler zu
  // ersetzen - ein Funktionsumbau der Statusanzeige, keine reine
  // Zustandsverschiebung, und genau das Risiko, vor dem der Auftrag warnt.
  const baumArt = (suchparams.get('baumart') || 'kollisionen') as
    'kollisionen' | 'planeraufgaben';

  // Der gewählte Knoten des Baums (nur Blätter sind wählbar) — für
  // 'gewerkpaar' der zusammengesetzte Schlüssel
  // 'geschoss|bereich|quadrant|gewerkA|gewerkB', der die Liste einschränkt.
  const baumGewaehlt = suchparams.get('knoten');

  const baumArtWaehlen = (a: 'kollisionen' | 'planeraufgaben') => {
    const neu = new URLSearchParams(suchparams);
    if (a === 'kollisionen') neu.delete('baumart'); else neu.set('baumart', a);
    neu.delete('knoten');
    setSuchparams(neu, { replace: true });
  };

  const baumKnotenWaehlen = (schluessel: string, ebene: string) => {
    if (ebene === 'aufgabe') {
      // Eine Planeraufgabe im Baum öffnen: in den Reiter wechseln, in dem
      // sie bearbeitet wird — dieselbe Detailkarte wie beim Anklicken in
      // der Liste dort.
      reiterWaehlen('planeraufgaben');
      setPlaneraufgabeOffen(schluessel);
      return;
    }

    const neu = new URLSearchParams(suchparams);
    neu.set('knoten', schluessel);
    setSuchparams(neu, { replace: true });
  };

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7 — DIE FILTERLEISTE UND DIE LISTE DES PLANERARBEITSPLATZES
  // ════════════════════════════════════════════════════════════════════
  const filter: Filter = {
    loesungsart: (suchparams.get('loesungsart') || '').split(',').filter(Boolean),
    status: (suchparams.get('status') || '').split(',').filter(Boolean),
    befund: (suchparams.get('befund') || '').split(',').filter(Boolean),
    gewerkWeicht: suchparams.get('gewerkWeicht') || '',
    gewerkBleibt: suchparams.get('gewerkBleibt') || '',
    korb: suchparams.get('korbf') || '',
    mindestDurchdringung: suchparams.get('mindestDurchdringung') || '',
    suche: suchparams.get('fsuche') || '',
    nurMitAnweisung: suchparams.get('nurMitAnweisung') === 'true',
    nurAutomatisch: suchparams.get('nurAutomatisch') === 'true',
    sortierung: suchparams.get('sortierung') || 'durchdringung',
  };

  const listeSeite = Number(suchparams.get('seite') || '0');

  const filterAendern = (f: Filter) => {
    const neu = new URLSearchParams(suchparams);
    const setzenOderLoeschen = (schluessel: string, wert: string) => {
      if (wert) neu.set(schluessel, wert); else neu.delete(schluessel);
    };

    setzenOderLoeschen('loesungsart', f.loesungsart.join(','));
    setzenOderLoeschen('status', f.status.join(','));
    setzenOderLoeschen('befund', f.befund.join(','));
    setzenOderLoeschen('gewerkWeicht', f.gewerkWeicht);
    setzenOderLoeschen('gewerkBleibt', f.gewerkBleibt);
    setzenOderLoeschen('korbf', f.korb);
    setzenOderLoeschen('mindestDurchdringung', f.mindestDurchdringung);
    setzenOderLoeschen('fsuche', f.suche);
    setzenOderLoeschen('nurMitAnweisung', f.nurMitAnweisung ? 'true' : '');
    setzenOderLoeschen('nurAutomatisch', f.nurAutomatisch ? 'true' : '');
    setzenOderLoeschen('sortierung',
      f.sortierung === 'durchdringung' ? '' : f.sortierung);
    neu.delete('seite');

    setSuchparams(neu, { replace: true });
  };

  const listeSeiteAendern = (s: number) => {
    const neu = new URLSearchParams(suchparams);
    if (s) neu.set('seite', String(s)); else neu.delete('seite');
    setSuchparams(neu, { replace: true });
  };

  const [liste, setListe] = useState<any | null>(null);
  const [listeLaedt, setListeLaedt] = useState(false);

  // Zählt hoch, um die Liste ausserhalb von Filter/Seite/Baum-Änderung neu
  // zu laden - nach einer Sammelaktion oder einer Einzelentscheidung, deren
  // Ergebnis die Liste sonst nicht widerspiegeln würde.
  const [listeVersion, setListeVersion] = useState(0);
  const [ausgewaehlt, setAusgewaehlt] = useState<Set<string>>(new Set());
  const [ablehngruende, setAblehngruende] = useState<any[]>([]);

  // ════════════════════════════════════════════════════════════════════
  // DOKUMENT 36, ABSCHNITT C — KI-BEWERTUNG AUS DER KOORDINATION
  // ════════════════════════════════════════════════════════════════════
  //
  // Eigene Auswahl (Kollisions-Id, NICHT Anweisungs-Id wie 'ausgewaehlt'
  // oben) - eine Kollision ohne Anweisung soll trotzdem bewertbar sein.
  //
  // "Ein Stapel läuft NICHT synchron im Anfrageweg" (Auftrag Dokument 36):
  // Der Server nimmt höchstens eine Handvoll Kollisionen je Aufruf an (siehe
  // CompletionEndpoints.KollisionsbewertungHoechstzahlJeAufruf, 120 s
  // KI-Zeitlimit je Kollision gegen 300 s Proxy-Zeitfenster) - ein "Stapel
  // über eine Auswahl" ruft den Endpunkt deshalb WIEDERHOLT mit je EINER
  // Kollision auf, genau wie "Vorschlag erzeugen" in AiCompletion.tsx schon
  // heute EINEN Aufruf mit sichtbarer Sekundenanzeige zeigt - hier als
  // Fortschritt "Kollision X von N" mit Abbruch ZWISCHEN zwei Aufrufen.
  const [kiAusgewaehlt, setKiAusgewaehlt] = useState<Set<string>>(new Set());
  const [kiFortschritt, setKiFortschritt] =
    useState<{ aktuell: number; gesamt: number } | null>(null);
  const kiAbbrechenRef = useRef(false);

  /** Dieselben Felder wie `KoordinationEndpoints.KiBewertungAnzeige` (Server) - für die optimistische Zeilenaktualisierung ohne Neuladen. */
  const kiBewertungAusItem = (item: CompletionPlanItem | null) => {
    if (!item) return null;
    const ext = (item.plan.operations[0]?.parameters?.extensions ?? {}) as Record<string, string | null>;
    let pruefpunkte: string[] = [];
    try { pruefpunkte = JSON.parse(ext.pruefpunkteJson ?? '[]'); } catch { /* leer bleibt leer */ }
    return {
      planId: item.plan.id,
      status: item.status === 'PROPOSED' ? 'vorgeschlagen' : item.status.toLowerCase(),
      bewertung: ext.bewertung ?? null,
      massnahme: ext.massnahme ?? null,
      weicher: ext.weicher ?? null,
      weicherUniqueId: ext.weicherUniqueId ?? null,
      richtung: ext.richtung ?? null,
      betragMm: ext.betragMm ?? null,
      risiko: ext.risiko ?? null,
      zuversicht: ext.zuversicht ?? null,
      begruendung: ext.begruendung ?? null,
      pruefpunkte,
      angelegtAm: item.updatedAt,
    };
  };

  const kiZeileAktualisieren = (kollisionId: string, kiBewertung: unknown) => {
    setListe((bisher: any) => {
      if (!bisher) return bisher;
      const schluessel = 'Zeilen' in bisher ? 'Zeilen' : 'zeilen';
      const zeilen = (bisher[schluessel] ?? []) as any[];
      return {
        ...bisher,
        [schluessel]: zeilen.map((z: any) =>
          z.id === kollisionId ? { ...z, kiBewertung } : z),
      };
    });
  };

  /**
   * Fordert für eine oder mehrere Kollisionen eine KI-Bewertung an - EIN
   * Aufruf je Kollision, nacheinander (siehe Kommentar oben an
   * `kiAusgewaehlt`). Ein Klick auf "KI-Bewertung anfordern" in der Zeile
   * ruft dies mit genau einer Id auf; der Stapel-Knopf mit der aktuellen
   * Auswahl.
   */
  const kiBewertungAnfordern = async (kollisionIds: string[]) => {
    if (!projekt || kollisionIds.length === 0) return;

    kiAbbrechenRef.current = false;
    setKiFortschritt({ aktuell: 0, gesamt: kollisionIds.length });

    for (let i = 0; i < kollisionIds.length; i++) {
      if (kiAbbrechenRef.current) break;
      const kollisionId = kollisionIds[i];

      try {
        const antwort = await api.koordinationKiBewertungAnfordern(
          projekt.id, [kollisionId]);
        const ergebnis = antwort.ergebnisse[0];

        if (ergebnis?.ok) {
          kiZeileAktualisieren(kollisionId, kiBewertungAusItem(ergebnis.item));
        } else {
          setMeldung(
            `Kollision ${kollisionId.slice(0, 8)}: `
            + `${ergebnis?.fehler ?? 'KI-Bewertung fehlgeschlagen.'}`);
        }
      } catch (e) {
        setMeldung(e instanceof Error ? e.message : String(e));
      } finally {
        setKiFortschritt({ aktuell: i + 1, gesamt: kollisionIds.length });
      }
    }

    setKiAusgewaehlt(new Set());
    // Die Fortschrittsanzeige bleibt kurz stehen, statt sofort zu
    // verschwinden - sonst wirkt "1 von 1" wie gar nicht erst gelaufen.
    setTimeout(() => setKiFortschritt(null), 1500);
  };

  const kiBewertungAbbrechen = () => { kiAbbrechenRef.current = true; };

  // ════════════════════════════════════════════════════════════════════
  // DIE AUFNAHME LÄUFT JE GEWERKEMODELL — ein Auftrag je Modell
  // ════════════════════════════════════════════════════════════════════
  //
  // Jedes Fachmodell ist ein eigenes Revit-Dokument. Der Lauf im
  // Lüftungsmodell liest die Lüftung vollständig und die übrigen Gewerke
  // gar nicht — sie kommen aus ihrem eigenen Lauf.
  //
  // Die Fremdmodelle liest JEDER Lauf mit: Sie bilden die Begrenzung, und
  // ohne Rohdecke und Abhangdecke gibt es kein Höhenband.
  //
  // Alle Läufe eines Tages ergänzen DENSELBEN Stand. Liefen sie in
  // getrennte Stände, könnte Lüftung nie mit Heizung kollidieren.
  const quellen = (projekt?.modelle ?? []).filter(
    (m) => m.isActive !== false && (m.rollen ?? []).length > 0);

  const aufnehmen = async (m: Modell) => {
    if (!projekt) return;

    setLaeuft(m.id);
    setMeldung(null);

    try {
      // Die Profilfassung liegt eine Ebene tiefer: profile[].versions[].id.
      // Ein Griff auf die oberste Ebene liefert undefined - und der Auftrag
      // scheiterte dann mit einer Meldung ueber das Profil statt ueber den
      // Griff.
      const profile = await api.profile(projekt.id, m.revitVersion);
      const profilId = profile.flatMap((x) => x.versions)[0]?.id ?? null;

      if (!profilId) {
        setMeldung(`Kein freigegebenes Exportprofil für Revit `
                 + `${m.revitVersion}. Ohne Profil lässt sich kein Auftrag `
                 + `anlegen.`);
        return;
      }

      const auftrag = await api.auftragAnlegen({
        projectId: projekt.id,
        exportProfileVersionId: profilId,
        modelIds: [m.id],

        // 130 — die Koordinationsaufnahme.
        kinds: [130],
      });

      setMeldung(`${m.name}: Auftrag ${auftrag.jobNumber} angelegt. Er liest `
               + `Achse und Bounding Box aller Bauteile, dazu die `
               + `Fremdmodelle als Begrenzung.`);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // DIE SUCHE — ueber den Rasterindex, nicht ueber alle Paare
  // ════════════════════════════════════════════════════════════════════
  //
  // 173.639 Bauteile ergaeben 15 Milliarden Paare. Zwei Bauteile koennen
  // sich nur durchdringen, wenn sie eine Rasterzelle teilen — verglichen
  // wird deshalb nur innerhalb einer Zelle. Das verwirft kein Paar, das
  // sich ueberlappt: Wer sich ueberlappt, teilt zwangslaeufig eine Zelle.
  // ════════════════════════════════════════════════════════════════════
  // DER ARBEITSVORRAT
  // ════════════════════════════════════════════════════════════════════
  //
  // Er stand hier nicht, und das war der Grund fuer 'der
  // Koordinationsbereich bringt keinen Mehrwert'. 318.000 Durchdringungen
  // mit derselben Einstufung sind keine Liste, sondern ein Haufen.
  //
  // Geladen werden zuerst nur die ZAEHLWERTE - acht Zeilen. Die Einzelfaelle
  // eines Korbes kommen erst, wenn jemand ihn anklickt, und dann
  // seitenweise.
  const korbLaden = async (korbSchluessel: string) => {
    if (!projekt) return;

    setKorb(korbSchluessel);
    setKorbseite(0);

    try {
      setVorrat(await api.koordinationArbeitsvorrat(
        projekt.id, { korb: korbSchluessel, seite: 0, groesse: 50 }));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  const korbBlaettern = async (seite: number) => {
    if (!projekt || !korb) return;

    setKorbseite(seite);

    try {
      setVorrat(await api.koordinationArbeitsvorrat(
        projekt.id, { korb, seite, groesse: 50 }));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // EIN VORSCHLAG IM DETAIL
  // ════════════════════════════════════════════════════════════════════
  //
  // Gemeldet: "keine details bei klick, man kann nichts zuordnen". Die Liste
  // zeigte "G04 | klt | seitlich_x | 569 mm" — und das sagt nicht, WELCHES
  // Rohr. Ohne Modellname und ElementId ist ein Vorschlag im Modell nicht
  // wiederzufinden und damit nicht umsetzbar.
  //
  // Geholt wird auf Klick und nicht mit der Liste: Das Detail löst beide
  // Bauteile auf und liest das Höhenband dazu. Bei 100 Zeilen je Seite wären
  // das 100 Auflösungen für eine, die jemand anschaut.
  const detailLaden = async (kollisionId: string) => {
    if (!projekt) return;

    // Nochmal auf dieselbe Zeile klicken schliesst sie.
    if (detail?.kollision?.id === kollisionId) {
      setDetail(null);
      return;
    }

    setDetailLaedt(kollisionId);
    setDetail(null);

    try {
      setDetail(await api.koordinationKollision(projekt.id, kollisionId));
    } catch (e) {
      merkeFehler('Detail')(e);
    } finally {
      setDetailLaedt(null);
    }
  };

  const suchen = async () => {
    if (!projekt) return;

    setSucht(true);
    setSuche(null);

    try {
      setSuche(await api.koordinationSuchen(projekt.id));

      api.koordinationKollisionen(projekt.id).then(setK)
        .catch((e) => setK(merkeFehler('Kollisionen')(e)));
      api.koordinationBericht(projekt.id).then(setBericht)
        .catch((e) => setBericht(merkeFehler('Bericht')(e)));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setSucht(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // STUFE 2 — wer weicht aus, wohin, und was kostet es
  // ════════════════════════════════════════════════════════════════════
  //
  // Die Hoehenbaender zuerst. 'Die Heizungsleitung 120 mm tiefer legen'
  // klingt nach einer Loesung und ist keine, wenn 80 mm darunter die
  // Abhangdecke sitzt.
  const vorschlagen = async () => {
    if (!projekt) return;

    setRechnet(true);
    setStufe2(null);

    try {
      setStufe2(await api.koordinationVorschlagen(projekt.id));
      setVf((f) => ({ ...f, seite: 1 }));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setRechnet(false);
    }
  };

  useEffect(() => {
    if (!projekt) return;

    setLadefehler({});

    api.koordinationArbeitsvorrat(projekt.id).then(setVorrat)
      .catch((e) => setVorrat(merkeFehler('Arbeitsvorrat')(e)));

    api.koordinationGrundlagen(projekt.id)
      .then((x) => { setD(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));

    api.koordinationKollisionen(projekt.id).then(setK)
      .catch((e) => setK(merkeFehler('Kollisionen')(e)));
    api.koordinationBericht(projekt.id).then(setBericht)
      .catch((e) => setBericht(merkeFehler('Bericht')(e)));
    api.koordinationAbstaende(projekt.id).then(setAbstaende)
      .catch((e) => setAbstaende(merkeFehler('Abstände')(e)));

    api.koordinationAblehngruende(projekt.id).then(setAblehngruende)
      .catch((e) => merkeFehler('Ablehngründe')(e));
  }, [api, projekt?.id]);

  // Die Vorschläge hängen an den Filtern und werden bei jeder Änderung neu
  // geholt. Im Browser zu filtern ginge nicht: Es sind zehntausende Zeilen,
  // und geladen wird immer nur eine Seite.
  useEffect(() => {
    if (!projekt) return;

    api.koordinationVorschlaege(projekt.id, {
      befund: vf.befund, gewerk: vf.gewerk, geschoss: vf.geschoss,
      bereich: vf.bereich, richtung: vf.richtung,
      seite: vf.seite, proSeite: 100,
    }).then(setVorschlaege)
      .catch((e) => setVorschlaege(merkeFehler('Vorschläge')(e)));
  }, [api, projekt?.id, vf]);

  // ════════════════════════════════════════════════════════════════════
  // LÖSUNGEN UND PLANERAUFGABEN (Loesungsableitung)
  // ════════════════════════════════════════════════════════════════════
  const ladeLoesungen = () => {
    if (!projekt) return;

    api.koordinationLoesungen(projekt.id).then(setLoesungen)
      .catch((e) => setLoesungen(merkeFehler('Lösungen')(e)));
  };

  const ladePlaneraufgaben = () => {
    if (!projekt) return;

    api.koordinationPlaneraufgaben(projekt.id, {
      status: planeraufgabeStatus || undefined,
    }).then(setPlaneraufgaben)
      .catch((e) => setPlaneraufgaben(merkeFehler('Planeraufgaben')(e)));
  };

  useEffect(ladeLoesungen, [api, projekt?.id]);

  useEffect(ladePlaneraufgaben, [api, projekt?.id, planeraufgabeStatus]);

  useEffect(() => {
    if (!projekt || !planeraufgabeOffen) { setPlaneraufgabeDetail(null); return; }

    api.koordinationPlaneraufgabe(projekt.id, planeraufgabeOffen)
      .then(setPlaneraufgabeDetail)
      .catch((e) => setPlaneraufgabeDetail(merkeFehler('Planeraufgabe')(e)));
  }, [api, projekt?.id, planeraufgabeOffen]);

  const loesungenAbleiten = async () => {
    if (!projekt) return;

    setAbleitetLaeuft(true);

    try {
      const r = await api.koordinationLoesungenAbleiten(projekt.id);
      setMeldung(
        `${z(r.anweisungen)} Anweisungen (${z(r.neu)} neu, `
        + `${z(r.aktualisiert)} aktualisiert), ${z(r.planeraufgaben)} `
        + 'Planeraufgaben.',
      );
      ladeLoesungen();
      ladePlaneraufgaben();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setAbleitetLaeuft(false);
    }
  };

  const planeraufgabeEntscheiden = async (status: string) => {
    if (!projekt || !planeraufgabeOffen) return;

    try {
      await api.koordinationPlaneraufgabeEntscheiden(
        projekt.id, planeraufgabeOffen,
        { status, entscheidung: planeraufgabeEntscheidungstext },
      );
      setPlaneraufgabeEntscheidungstext('');
      setPlaneraufgabeOffen(null);
      ladePlaneraufgaben();
      ladeLoesungen();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  // Erzeugt die Erläuterung und lädt danach das Detail neu -- der Endpunkt
  // speichert selbst (siehe Loesungsableitung.ErlaeuternEinzelnAsync), die
  // Karte zeigt nur, was jetzt in der Datenbank steht.
  const planeraufgabeErlaeutern = async () => {
    if (!projekt || !planeraufgabeOffen) return;

    setPlaneraufgabeErlaeuternLaeuft(true);

    try {
      await api.koordinationPlaneraufgabeErlaeutern(
        projekt.id, planeraufgabeOffen);

      const neu = await api.koordinationPlaneraufgabe(
        projekt.id, planeraufgabeOffen);
      setPlaneraufgabeDetail(neu);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setPlaneraufgabeErlaeuternLaeuft(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7 — DER BAUM LADEN (nur im Reiter „Arbeiten“)
  // ════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!projekt || reiter !== 'arbeiten') return;

    setBaumLaedt(true);

    api.koordinationArbeitsbaum(projekt.id, { art: baumArt })
      .then((r) => { setBaum(r); setBaumLaedt(false); })
      .catch((e) => { merkeFehler('Baum')(e); setBaumLaedt(false); });
  }, [api, projekt?.id, reiter, baumArt]);

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7b — OHNE KNOTEN, OHNE FILTER GIBT ES DIE LISTE NICHT
  // ════════════════════════════════════════════════════════════════════
  //
  // Der Server weist eine Anfrage ohne jedes Kriterium mit 400 ab (siehe
  // KoordinationEndpoints.cs) — hier zusätzlich schon davor: Ohne Kriterium
  // wird gar nicht erst gefragt, sondern der Hinweis „Links einen Knoten
  // wählen oder filtern“ gezeigt.
  const hatKriterium = !!baumGewaehlt
    || filter.loesungsart.length > 0
    || filter.status.length > 0
    || filter.befund.length > 0
    || !!filter.gewerkWeicht
    || !!filter.gewerkBleibt
    || !!filter.korb
    || !!filter.mindestDurchdringung
    || !!filter.suche
    || filter.nurMitAnweisung
    || filter.nurAutomatisch;

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7 — DIE LISTE LADEN (Baum-Auswahl + Filterleiste + Seite)
  // ════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!projekt || reiter !== 'arbeiten') return;

    if (!hatKriterium) {
      setListe(null);
      setListeLaedt(false);
      return;
    }

    setListeLaedt(true);

    // Der Baum liefert 'geschoss|bereich|quadrant|gewerkA|gewerkB' (siehe
    // KoordinationEndpoints.Zweigschluessel) - dieselben Trennzeichen hier
    // wieder auseinandergenommen.
    const bTeile = baumGewaehlt ? baumGewaehlt.split('|') : null;

    api.koordinationKollisionenListe(projekt.id, {
      geschoss: bTeile?.[0] || undefined,
      bereich: bTeile?.[1] || undefined,
      quadrant: bTeile?.[2] || undefined,
      gewerkWeicht: bTeile?.[3] || filter.gewerkWeicht || undefined,
      gewerkBleibt: bTeile?.[4] || filter.gewerkBleibt || undefined,
      loesungsart: filter.loesungsart.join(',') || undefined,
      status: filter.status.join(',') || undefined,
      befund: filter.befund.join(',') || undefined,
      korb: filter.korb || undefined,
      mindestDurchdringung: filter.mindestDurchdringung
        ? Number(filter.mindestDurchdringung) : undefined,
      suche: filter.suche || undefined,
      nurMitAnweisung: filter.nurMitAnweisung || undefined,
      nurAutomatisch: filter.nurAutomatisch || undefined,
      sortierung: filter.sortierung,
      seite: listeSeite,
      groesse: 50,
    }).then((r) => { setListe(r); setListeLaedt(false); setAusgewaehlt(new Set()); })
      .catch((e) => { merkeFehler('Kollisionsliste')(e); setListeLaedt(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projekt?.id, reiter, baumGewaehlt, JSON.stringify(filter), listeSeite, listeVersion]);

  // ════════════════════════════════════════════════════════════════════
  // BATCH 7 — DIE SAMMELAKTION UND DIE EINZELENTSCHEIDUNG DER LISTE
  // ════════════════════════════════════════════════════════════════════
  const listeSammelaktion = async (
    status: 'angenommen' | 'abgelehnt', ablehngrund?: string, angabe?: string,
  ) => {
    if (!projekt || ausgewaehlt.size === 0) return;

    try {
      const r = await api.koordinationAnweisungenSammelstatus(projekt.id, {
        ids: Array.from(ausgewaehlt), status, ablehngrund, angabe,
      });
      setMeldung(`${z(r.entschieden)} von ${z(r.angefordert)} Anweisungen `
               + `auf '${status}' gesetzt.`);
      setAusgewaehlt(new Set());
      setListeVersion((v) => v + 1);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  const listeZeileKlicken = (kollisionId: string) => detailLaden(kollisionId);

  const listeEntscheiden = async (
    status: 'angenommen' | 'umgesetzt' | 'abgelehnt',
  ) => {
    const anweisungId = detail?.anweisung?.id;
    if (!anweisungId) return;

    if (status === 'abgelehnt') {
      // Eine Ablehnung braucht einen Grund aus der Liste (GrundPruefenAsync)
      // - dieselbe Sammelaktion mit genau einer Id übernimmt das, statt
      // eine zweite Eingabemaske zu bauen.
      setAusgewaehlt(new Set([anweisungId]));
      return;
    }

    try {
      await api.koordinationAnweisungStatus(anweisungId, { status });
      setMeldung(`Anweisung auf '${status}' gesetzt.`);

      // Die Detailkarte zeigt den neuen Status erst nach einem erneuten
      // Laden - einmal schliessen, einmal wieder öffnen (detailLaden ist
      // ein Umschalter: derselbe Aufruf schliesst, wenn schon offen).
      const id = detail.kollision.id;
      await detailLaden(id);
      await detailLaden(id);
      setListeVersion((v) => v + 1);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  const listeNaechste = () => {
    const zeilen = liste?.Zeilen ?? liste?.zeilen ?? [];
    const zeilenIds: string[] = zeilen.map((r: any) => r.id);
    const aktuell = detail?.kollision?.id;
    const index = aktuell ? zeilenIds.indexOf(aktuell) : -1;
    const naechste = zeilenIds[(index + 1) % Math.max(1, zeilenIds.length)];

    if (naechste) detailLaden(naechste);
  };

  if (fehler) {
    return (
      <>
        <PageHeader titel="Koordination" />
        <ErrorState
          meldung="Die Koordinationsdaten konnten nicht geladen werden."
          technisch={fehler}
        />
      </>
    );
  }

  if (!projekt || !d) {
    return (
      <>
        <PageHeader
          titel="Koordination"
          beschreibung="Kollisionen, Einstufung und die Abarbeitung je Planer."
        />
        <LoadingState text="Koordinationsdaten werden geladen …" zeilen={8} spalten={5} />
      </>
    );
  }

  const z = (x: number) => (x ?? 0).toLocaleString('de-DE');
  const ohneLauf = (d.staende ?? []).length === 0;

  const zeilen = liste?.Zeilen ?? liste?.zeilen ?? [];
  const zeilenGesamt = liste?.ZeilenGesamt ?? liste?.zeilenGesamt ?? 0;

  return (
    <>
      <PageHeader
        titel="Koordination"
        beschreibung={'Nicht das Finden ist schwer, sondern das Aussortieren. Die '
          + 'Einstufung weiß, wo eine Aussparung geplant ist, und stuft die '
          + 'Durchdringung als vorgesehen ein statt als Fehler.'}
        meta={ohneLauf
          ? 'noch kein Lauf'
          : `${(d.staende ?? []).length} Stände`}
      >
        <Reiter gewaehlt={reiter} onWaehlen={reiterWaehlen} />
      </PageHeader>

      {/* ══════════════════════════════════════════════════════════════════
          WAS SICH NICHT LADEN LIESS — ÜBER ALLEM ANDEREN

          Diese Karte ist nur da, wenn etwas schiefging. Sie steht über den
          Reitern, weil eine leere Liste ohne diese Meldung nach "nichts
          gefunden" aussieht — und genau diese Verwechslung hat den
          Vorschlagsendpunkt drei Tage lang unentdeckt kaputt sein lassen.
          ══════════════════════════════════════════════════════════════════ */}
      {Object.keys(ladefehler).length > 0 && (
        <>
          <ErrorState
            titel={`${Object.keys(ladefehler).length} Bereich(e) ließen sich nicht laden`}
            meldung={'Die Listen unten sind deshalb leer oder unvollständig — das ist '
              + 'ein Fehler und nicht das Ergebnis. Bitte melden.'}
          />
          <DataTable
            name="Nicht geladene Bereiche"
            spalten={[
              {
                schluessel: 'bereich', titel: 'Bereich', breite: '180px',
                wert: (f: { was: string; text: string }) => f.was,
                inhalt: (f: { was: string; text: string }) => <strong>{f.was}</strong>,
              },
              {
                schluessel: 'meldung', titel: 'Meldung',
                wert: (f: { was: string; text: string }) => f.text,
              },
            ]}
            zeilen={Object.entries(ladefehler).map(([was, text]) => ({ was, text }))}
            schluessel={(f) => f.was}
            suche={false}
            spaltenwahl={false}
            dicht
          />
        </>
      )}

      {/* ── Arbeiten ─────────────────────────────────────────────────────
          Links der Baum, rechts Filterleiste + Liste + Detailkarte — der
          NEUE Planerarbeitsplatz. Darunter, unverändert: die bestehenden
          Karten „Kollisionen und Einstufung“ und „Abarbeitung“. */}
      {reiter === 'arbeiten' && (
        <>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start',
                        flexWrap: 'wrap' }}>
            <Baum
              baum={baum?.Wurzel ?? baum?.wurzel}
              art={baumArt}
              onArtChange={(a) => baumArtWaehlen(a as 'kollisionen' | 'planeraufgaben')}
              gewaehlt={baumGewaehlt}
              onWaehlen={baumKnotenWaehlen}
              laedt={baumLaedt}
            />

            <div style={{ flex: '1 1 640px', minWidth: 320 }}>
              <Filterleiste
                filter={filter}
                onChange={filterAendern}
                loesungsarten={d.loesungsarten ?? []}
                koerbe={[]}
                gezeigt={zeilen.length}
                gesamt={zeilenGesamt}
              />

              {!hatKriterium && (
                <EmptyState
                  titel="Noch nichts eingegrenzt."
                  erklaerung={'Links einen Knoten wählen oder mindestens einen Filter '
                    + 'setzen — ohne beides wäre das die ganze Liste des Standes.'}
                />
              )}

              {hatKriterium && listeLaedt && (
                <LoadingState text="Kollisionen werden geladen …" zeilen={8} spalten={6} />
              )}

              {hatKriterium && !listeLaedt && (
                <Kollisionsliste
                  api={api} projektId={projekt.id}
                  zeilen={zeilen}
                  zeilenGesamt={zeilenGesamt}
                  seite={listeSeite}
                  groesse={50}
                  onSeiteChange={listeSeiteAendern}
                  ausgewaehlt={ausgewaehlt}
                  onAuswahlChange={setAusgewaehlt}
                  ablehngruende={ablehngruende}
                  onSammelaktion={listeSammelaktion}
                  geoeffnet={detail?.kollision?.id ?? null}
                  onZeileKlick={listeZeileKlicken}
                  detail={detail}
                  detailLaedt={detailLaedt}
                  onEntscheiden={listeEntscheiden}
                  onNaechste={listeNaechste}
                  kiAusgewaehlt={kiAusgewaehlt}
                  onKiAuswahlChange={setKiAusgewaehlt}
                  kiFortschritt={kiFortschritt}
                  onKiBewertungAnfordern={kiBewertungAnfordern}
                  onKiBewertungAbbrechen={kiBewertungAbbrechen}
                />
              )}
            </div>
          </div>

          <h2 style={{ marginTop: 24 }}>Kollisionen und Einstufung</h2>
          <KollisionenUndEinstufung
            bestand={d.bestand}
            luecken={d.luecken ?? []}
            k={k}
          />

          <h2>Abarbeitung</h2>
          <Arbeitsvorrat
            api={api} projektId={projekt.id}
            vorrat={vorrat}
            korb={korb}
            korbseite={korbseite}
            onKorbLaden={korbLaden}
            onKorbBlaettern={korbBlaettern}
            detail={detail}
            detailLaedt={detailLaedt}
            onDetailLaden={detailLaden}
            stufe2={stufe2}
            rechnet={rechnet}
            onVorschlagen={vorschlagen}
            vorschlaege={vorschlaege}
            vf={vf}
            onVfChange={setVf}
            abstaende={abstaende}
          />
        </>
      )}

      {/* ── Planeraufgaben ───────────────────────────────────────────── */}
      {reiter === 'planeraufgaben' && (
        <Planeraufgaben
          planeraufgaben={planeraufgaben}
          status={planeraufgabeStatus}
          onStatusChange={setPlaneraufgabeStatus}
          offenId={planeraufgabeOffen}
          onToggle={(id) => setPlaneraufgabeOffen(
            planeraufgabeOffen === id ? null : id)}
          detail={planeraufgabeDetail}
          entscheidungstext={planeraufgabeEntscheidungstext}
          onEntscheidungstextChange={setPlaneraufgabeEntscheidungstext}
          onEntscheiden={planeraufgabeEntscheiden}
          onErlaeutern={planeraufgabeErlaeutern}
          erlaeuternLaeuft={planeraufgabeErlaeuternLaeuft}
        />
      )}

      {/* ── Auswertung ───────────────────────────────────────────────── */}
      {reiter === 'auswertung' && (
        <Auswertung
          loesungen={loesungen}
          ableitetLaeuft={ableitetLaeuft}
          onAbleiten={loesungenAbleiten}
          fehler={ladefehler.Lösungen}
          bericht={bericht}
        />
      )}

      {/* ── Grundlagen ───────────────────────────────────────────────── */}
      {reiter === 'grundlagen' && (
        <Grundlagen
          projektId={projekt.id}
          ohneLauf={ohneLauf}
          quellen={quellen}
          laeuft={laeuft}
          onAufnehmen={aufnehmen}
          meldung={meldung}
          suche={suche}
          sucht={sucht}
          onSuchen={suchen}
          d={d}
        />
      )}
    </>
  );
}
